import React, { useMemo, useState } from 'react'

import { Field, Form as FormikForm, Formik, FieldProps } from 'formik'
import * as Yup from 'yup'

import { FormProps, LayoutItem } from '../../../ana-toolbox'
import { FormLayout } from '../../components/form-layout/form-layout'
import { LegalRepresentativeDialog } from '../../components/legal-representative-dialog/legal-representative-dialog'
import { MaskedDateInputProps } from '../../components/masked-date-input/masked-date-input'
import { SubmitButton } from '../../components/submit-button/submit-button'
import { Translate } from '../../components/translate/translate'
import { Type, isPatientUnderaged } from '../../utils/entities/Form'
import { personalInformationValidationSchema } from '../../utils/fields/personalInformation'
import { createField } from '../../utils/helpers/createField'
import { getFormInitialErrors } from '../../utils/helpers/getFormInitialErrors'
import { AddressLayoutItem } from '../../utils/shared/AddressLayoutItem'
import { SectionTitle } from '../../utils/shared/SectionTitle'
import { combinedFields } from '../../utils/yup/main'

const personalInformationLayout = ({
  birthdayProps,
  canBeFilledForDifferentPerson,
}: {
  birthdayProps: Partial<MaskedDateInputProps>
  canBeFilledForDifferentPerson?: boolean
}): LayoutItem[] => {
  const contactPersonFields: LayoutItem[] = [
    {
      type: 'field',
      field: combinedFields.filedForDifferentPerson,
      xs: 12,
    },
    {
      type: 'field',
      field: combinedFields.contactPersonFirstName,
      xs: 12,
    },
    {
      type: 'field',
      field: combinedFields.contactPersonLastName,
      xs: 12,
    },
    {
      type: 'field',
      field: combinedFields.contactPersonBirthday,
      xs: 12,
    },
    {
      type: 'custom',
      key: 'contactPersonStreet',
      xs: 12,
      render: ({ values, formHelpers }) => {
        const field = combinedFields.contactPersonStreet

        if (!field.visibleWhen(values)) return null

        return <AddressLayoutItem field={combinedFields.contactPersonStreet} formHelpers={formHelpers} {...field} />
      },
    },
    {
      type: 'field',
      field: combinedFields.contactPersonZipcode,
      xs: 12,
    },
    {
      type: 'field',
      field: combinedFields.contactPersonCity,
      xs: 12,
    },
    {
      type: 'field',
      field: combinedFields.contactPersonRelation,
      xs: 12,
    },
  ]

  const patientFields: LayoutItem[] = [
    {
      type: 'custom',
      key: 'section-title',
      xs: 12,
      render: ({ values }) => {
        return values.filedForDifferentPerson ? (
          <SectionTitle>
            <Translate>patient_personal_information_title</Translate>
          </SectionTitle>
        ) : null
      },
    },
    { type: 'field', field: combinedFields.gender, xs: 12 },
    {
      type: 'field',
      field: combinedFields.firstName,
      xs: 12,
      sm: 6,
    },
    {
      type: 'field',
      field: combinedFields.lastName,
      xs: 12,
      sm: 6,
    },
    {
      key: 'birthday',
      type: 'custom',
      xs: 12,
      sm: 6,
      render: () => {
        return createField({
          ...combinedFields.birthday,
          inputProps: {
            ...combinedFields.birthday.inputProps,
            ...birthdayProps,
          },
        })
      },
    },
    {
      type: 'field',
      field: combinedFields.placeOfBirth,
      xs: 12,
      sm: 6,
    },
    {
      type: 'field',
      field: combinedFields.bodyWeightInKg,
      xs: 12,
    },
    { type: 'field', field: combinedFields.bodyHeightInCm, xs: 12 },
    { type: 'field', field: combinedFields.occupation, xs: 12 },
  ]

  return canBeFilledForDifferentPerson ? [...contactPersonFields, ...patientFields] : patientFields
}

export const PersonalInformationForm = ({
  initialValues,
  portalAttr,
  onSubmit,
  loading,
  innerRef,
  canBeFilledForDifferentPerson,
}: FormProps & { canBeFilledForDifferentPerson: boolean }) => {
  const [isBirthdayFocused, setIsBirthdayFocused] = useState(false)
  const [ignoreNextBirthdayBlur, setIgnoreNextBirthdayBlur] = useState(false)
  const [showDialog, setShowDialog] = useState(false)

  const submitButton = (
    <Field>
      {(props: FieldProps) => {
        return (
          <SubmitButton onClick={() => props.form.submitForm()} disabled={!props.form.isValid} loading={loading}>
            <Translate>next</Translate>
          </SubmitButton>
        )
      }}
    </Field>
  )

  const legalRepresentativeModal = (
    <Field>
      {({ form: { setFieldValue, values } }: FieldProps<unknown, Type>) => {
        const handleConfirm = () => {
          setFieldValue('filedForDifferentPerson', true)
          window.scrollTo({ top: 0, behavior: 'smooth' })
          setShowDialog(false)
        }

        const handleReject = () => {
          const birthdayInput = document.querySelector<HTMLInputElement>('[name=birthday]')
          setIgnoreNextBirthdayBlur(true)

          setTimeout(() => {
            birthdayInput?.focus()
          }, 0)

          setShowDialog(false)
        }

        const isUnderaged = isPatientUnderaged(values)

        return (
          <LegalRepresentativeDialog
            isOpen={
              isUnderaged &&
              showDialog &&
              !isBirthdayFocused &&
              !ignoreNextBirthdayBlur &&
              !values.filedForDifferentPerson
            }
            onConfirm={handleConfirm}
            onReject={handleReject}
          />
        )
      }}
    </Field>
  )

  const layout = useMemo(() => {
    return personalInformationLayout({
      canBeFilledForDifferentPerson,
      birthdayProps: {
        onBlur: (e) => {
          if (isPatientUnderaged({ birthday: e.target.value })) {
            if (ignoreNextBirthdayBlur) {
              setIgnoreNextBirthdayBlur(false)
            } else {
              setShowDialog(true)
            }
          }

          setIsBirthdayFocused(false)
        },
        onFocus: () => {
          setIsBirthdayFocused(true)
        },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ignoreNextBirthdayBlur])

  const validationSchema = canBeFilledForDifferentPerson
    ? personalInformationValidationSchema
    : personalInformationValidationSchema.concat(
        Yup.object().shape({
          birthday: Yup.string().adult('patient_valid_age'),
        })
      )

  return (
    <Formik
      initialValues={initialValues}
      initialErrors={getFormInitialErrors(validationSchema, initialValues)}
      innerRef={innerRef}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <FormikForm>
        {canBeFilledForDifferentPerson && legalRepresentativeModal}

        <FormLayout portalAttr={portalAttr} items={layout} submitButton={submitButton} />
      </FormikForm>
    </Formik>
  )
}
